const LANG_LOCALE_MAP = {
  zh: 'zh-CN',
  id: 'id-ID',
  en: 'en-US'
};
export const DEFAULT_BUSINESS_TIME_ZONE = 'Asia/Jakarta';

export const getLanguageCode = (language = '') => {
  const code = String(language || '').split('-')[0].toLowerCase();
  return ['zh', 'id', 'en'].includes(code) ? code : 'id';
};

export const getDateLocale = (language = '') => LANG_LOCALE_MAP[getLanguageCode(language)] || 'id-ID';

export const formatLocalizedNumber = (value, language = '', options = {}) => {
  const numericValue = Number(value || 0);
  return (Number.isFinite(numericValue) ? numericValue : 0).toLocaleString(getDateLocale(language), options);
};

export const formatLocalizedCurrency = (value, language = '') => `Rp ${formatLocalizedNumber(value, language)}`;

export const formatLocalizedDateTime = (value, language = '', options = {}) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const { timeZone = DEFAULT_BUSINESS_TIME_ZONE, ...dateTimeOptions } = options;
  return date.toLocaleString(getDateLocale(language), {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...dateTimeOptions
  });
};

export const formatMonthLabel = (month, language = '') => {
  const numericMonth = Number(month);
  if (!Number.isFinite(numericMonth) || numericMonth < 1 || numericMonth > 12) {
    return String(month || '');
  }

  const languageCode = getLanguageCode(language);
  if (languageCode === 'zh') {
    return `${numericMonth}月`;
  }

  return new Date(2000, numericMonth - 1, 1).toLocaleString(getDateLocale(language), {
    month: 'long'
  });
};

export const formatUnitCount = (t, count) => t('format.unitsCount', {
  count: Number(count || 0),
  defaultValue: `${Number(count || 0)} units`
});

export const normalizeAutogeneratedDeviceName = (name, fallback = '') => {
  const raw = String(name || '').trim();
  const fallbackValue = String(fallback || '').trim();
  if (!raw) return fallbackValue;

  const autogeneratedMatch = raw.match(/^设备\s+(\d+)$/);
  if (autogeneratedMatch) {
    return autogeneratedMatch[1];
  }

  return raw.replace(/^设备\s+设备\s+/u, '设备 ');
};

export const getFilterDisplayName = (filter, language = '') => {
  const languageCode = getLanguageCode(language);
  const keyByLanguage = {
    zh: 'displayNameZh',
    en: 'displayNameEn',
    id: 'displayNameId'
  };
  return filter?.[keyByLanguage[languageCode]]
    || filter?.displayNameId
    || filter?.displayNameEn
    || filter?.displayNameZh
    || filter?.displayName
    || filter?.filterKey
    || '-';
};

export const getWaterTypeLabel = (t, waterType) => {
  const normalized = String(waterType || '').toLowerCase();
  if (normalized === 'pure' || normalized === '纯净水') {
    return t('waterTypes.pure', { defaultValue: 'Air murni' });
  }
  if (normalized === 'mineral' || normalized === '矿物质水') {
    return t('waterTypes.mineral', { defaultValue: 'Air mineral' });
  }
  return waterType || '-';
};

export const getTransactionDisplayType = (t, item = {}) => {
  const key = item.displayTypeKey || item.flowType || item.type;
  const rawDisplay = String(item.displayType || '');

  if (item.isStandalonePhysical || key === 'standalonePhysical' || rawDisplay.includes('实体卡独立')) {
    return t('transactions.displayType.standalonePhysical', { defaultValue: 'Pengambilan kartu fisik mandiri' });
  }
  if (item.isKudInitialCard || key === 'kudInitialCard' || rawDisplay.includes('首批KUD')) {
    return t('transactions.displayType.kudInitialCard', { defaultValue: 'Pengambilan kartu fisik KUD awal' });
  }
  if (String(item.payment_provider || '').toLowerCase() === 'payermax' || key === 'payermaxTopup' || rawDisplay.includes('PayerMax')) {
    return t('transactions.displayType.payermaxTopup', { defaultValue: 'Isi ulang PayerMax' });
  }
  if (item.balanceSource === 'SIGNUP_BONUS' || key === 'signupBonus') {
    return t('transactions.displayType.signupBonus', { defaultValue: 'Bonus pendaftaran' });
  }
  if (key === 'topup' || key === 'appTopup' || rawDisplay.includes('App充值')) {
    return t('transactions.displayType.appTopup', { defaultValue: 'Isi ulang App' });
  }
  if (key === 'dispense' || key === 'userDispense' || rawDisplay.includes('用户账户取水')) {
    return t('transactions.displayType.userDispense', { defaultValue: 'Pengambilan dari akun pengguna' });
  }
  return rawDisplay || key || '-';
};

export const getStatusLabel = (t, namespace, status, fallback = '-') => {
  const key = String(status || '').trim();
  if (!key) return fallback;
  return t(`${namespace}.${key}`, { defaultValue: key });
};

export const getInspectionItemText = (t, item = {}, field = 'title') => {
  const itemId = String(item.itemId || '').replace(/[.-]/g, '_');
  const fallback = item[field] || item.itemId || '-';
  if (!itemId) return fallback;
  return t(`inspectionItems.${itemId}.${field}`, { defaultValue: fallback });
};

export const getInspectionFreeText = (t, value) => {
  const text = String(value || '').trim();
  if (!text) return text;

  if (['无', 'none', 'None', '-'].includes(text)) {
    return t('common.none', { defaultValue: '-' });
  }

  if (['无异常', '正常', 'normal', 'Normal', 'OK', 'ok'].includes(text)) {
    return t('inspectionText.noIssue', { defaultValue: 'No issue' });
  }

  const noIssueMatch = text.match(/^完成\s+(\d+)\/(\d+)\s+项巡检，未发现异常。?$/);
  if (noIssueMatch) {
    return t('inspectionText.summaryNoIssue', {
      completed: noIssueMatch[1],
      total: noIssueMatch[2],
      defaultValue: `Completed ${noIssueMatch[1]}/${noIssueMatch[2]} inspection items, no issue found.`
    });
  }

  const abnormalMatch = text.match(/^完成\s+(\d+)\/(\d+)\s+项巡检，发现\s+(\d+)\s+项异常。?$/);
  if (abnormalMatch) {
    return t('inspectionText.summaryWithIssue', {
      completed: abnormalMatch[1],
      total: abnormalMatch[2],
      abnormal: abnormalMatch[3],
      defaultValue: `Completed ${abnormalMatch[1]}/${abnormalMatch[2]} inspection items, found ${abnormalMatch[3]} abnormal items.`
    });
  }

  return value;
};

export const getWatermarkLabel = (t, status) => {
  const key = status || 'unknown';
  return t(`watermarkStatus.${key}`, { defaultValue: status || '-' });
};

export const getIotAlertLabel = (t, errorCode, fallback = '') => {
  if (!errorCode) return fallback || '-';
  return t(`deviceAlertsPage.iotFaults.${errorCode}`, { defaultValue: fallback || errorCode });
};

export const getAlertMessage = (t, alert = {}) => {
  const deviceName = normalizeAutogeneratedDeviceName(
    alert.unit?.deviceName || alert.unit?.location,
    alert.unit?.deviceId || alert.unitId || '-'
  );
  const errorCode = alert.errorCode || '-';
  const faultLabel = getIotAlertLabel(t, alert.errorCode, alert.faultLabelZh || errorCode);

  if (alert.source === 'telemetry' && alert.sourceKey === 'ro_tds_over_50') {
    const value = String(alert.alertMessage || '').match(/\bTDS\s+(-?\d+(?:\.\d+)?)/i)?.[1] || '-';
    return t('deviceAlertsPage.messages.roTdsHigh', {
      value,
      threshold: 50,
      defaultValue: `RO water TDS exceeded 50 twice; latest reading: ${value}`
    });
  }

  if (alert.alertType === 'offline') {
    return t('deviceAlertsPage.messages.offline', {
      device: deviceName,
      minutes: 5,
      defaultValue: `${deviceName} has been offline for more than 5 minutes`
    });
  }

  if (alert.alertType === 'low_quality') {
    return t('deviceAlertsPage.messages.lowQuality', {
      errorCode,
      faultLabel,
      defaultValue: `Water quality warning: ${faultLabel}`
    });
  }

  if (alert.alertType === 'fault') {
    return t('deviceAlertsPage.messages.fault', {
      errorCode,
      faultLabel,
      defaultValue: `Device fault: ${faultLabel}`
    });
  }

  if (alert.alertType === 'maintenance') {
    return t('deviceAlertsPage.messages.maintenance', {
      errorCode,
      faultLabel,
      defaultValue: `Maintenance warning: ${faultLabel}`
    });
  }

  return alert.alertMessage || t('deviceAlertsPage.noMessage');
};
